import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  fetchGooglePlaceSuggestions,
  fetchGooglePlaceDetails,
  Suggestion,
  getAddressFromServer,
} from 'helpers/googlePlacesHelper';
import { SvgIcon } from 'helpers/SvgIcon';
import { v4 as uuidv4 } from 'uuid';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import useIsEditing from 'lib/hooks/useIsEditing';

export interface Location {
  address: string;
  lat: number | null;
  lng: number | null;
}

export interface LocationInputHandle {
  getLocationValue: () => Location | null;
  setLocationValue: (location: Location) => void;
  setValue?: (value: string) => void;
}

interface LocationInputProps {
  onLocationSelect: (location: Location) => void;
  placeholder?: Field<string>;
  className?: string;
  onClear?: () => void;
  designType?: 'mini' | 'default';
  disabledLiveLocation?: boolean;
  value?: Location;
  onEnter?: (location: Location) => void; // Added prop
}

const LocationInput = forwardRef<LocationInputHandle, LocationInputProps>(
  (
    {
      onLocationSelect,
      className,
      placeholder,
      onClear,
      designType = 'default',
      disabledLiveLocation = false,
      value,
      onEnter,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isConditionFocused, setIsConditionFocused] = useState(false);
    const [placesSessionId] = useState(uuidv4());
    const [location, setLocation] = useState<Location | null>(null);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const suggestionListRef = useRef<HTMLUListElement | null>(null);
    const DEBOUNCE_DELAY = 500;
    const isEditing = useIsEditing();

    useImperativeHandle(ref, () => ({
      getLocationValue: () => location,
      setLocationValue: (loc: Location) => {
        setLocation(loc);
        setInputValue(loc.address);
        setIsConditionFocused(!!loc.address);
      },
      setValue: (value: string) => {
        setInputValue(value);
        setIsConditionFocused(!!value);
      },
    }));

    useEffect(() => {
      if (value) {
        setLocation(value);
        setInputValue(value.address);
        setIsConditionFocused(!!value.address);
      }
    }, [value]);

    // Fetch suggestions
    useEffect(() => {
      let active = true;
      async function getSuggestions(input: string) {
        const results = await fetchGooglePlaceSuggestions(input, placesSessionId);
        if (active) {
          setSuggestions(results);
          if (designType === 'mini') {
            setShowSuggestions(results.length > 0);
          }
        }
      }

      if (inputValue && document.activeElement === inputRef.current) {
        const handler = setTimeout(() => {
          getSuggestions(inputValue);
        }, DEBOUNCE_DELAY);
        return () => {
          active = false;
          clearTimeout(handler);
        };
      } else {
        setSuggestions([]);
        return () => {
          active = false;
        };
      }
    }, [inputValue, placesSessionId]);

    useEffect(() => {
      const container = containerRef.current;

      const handleClickOutside = (event: MouseEvent) => {
        if (container && !container.contains(event.target as Node)) {
          setShowSuggestions(false);
          setHighlightedIndex(-1);
        }
      };

      const handleFocusOut = (event: FocusEvent) => {
        const relatedTarget = event.relatedTarget as Node | null;
        if (container && relatedTarget && !container.contains(relatedTarget)) {
          setShowSuggestions(false);
          setHighlightedIndex(-1);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      container?.addEventListener('focusout', handleFocusOut, true);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        container?.removeEventListener('focusout', handleFocusOut, true);
      };
    }, []);

    const handleSuggestionClick = async (suggestion: Suggestion) => {
      const details = await fetchGooglePlaceDetails(suggestion?.place_id || '', placesSessionId);
      if (details && details.geometry) {
        const selectedLocation: Location = {
          address: details.formatted_address,
          lat: details.geometry.location.lat,
          lng: details.geometry.location.lng,
        };
        setInputValue(details.formatted_address);
        setSuggestions([]);
        setLocation(selectedLocation);
        onLocationSelect(selectedLocation);
        // Do NOT call onEnter here
      }
      setShowSuggestions(false);
    };

    const handleCurrentLocationClick = () => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const data = await getAddressFromServer(lat, lng);
          const currentLoc: Location = { address: data, lat, lng };
          setInputValue(data);
          setSuggestions([]);
          setShowSuggestions(false);
          setLocation(currentLoc);
          onLocationSelect(currentLoc);
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Geolocation error: ' + error.message);
        },
        {
          timeout: 5000,
          maximumAge: 60000,
        }
      );
    };

    const handleBlur = () => {
      if (inputValue === '' || inputValue === null) {
        setIsConditionFocused(false);
      } else {
        setIsConditionFocused(true);
      }
    };

    const handleFocus = () => {
      if (!disabledLiveLocation) {
        setShowSuggestions(true);
      }
      // if (inputValue !== '') {
      setIsConditionFocused(true);
      // }
    };

    return (
      <div
        ref={containerRef}
        className={`relative ${className ?? ''} ${designType === 'mini' ? 'w-full' : ''}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div className="flex-1 relative bg-white lg:rounded-tr-sm rounded-bl-sm lg:rounded-bl-none rounded-br-sm focus:outline-2 focuus:outline-blue-base ">
          <input
            type="text"
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => handleFocus()}
            onBlur={() => handleBlur()}
            readOnly={isEditing}
            aria-label={inputValue ? inputValue : placeholder?.value}
            onKeyDown={async (e) => {
              const totalOptions = disabledLiveLocation
                ? suggestions.length
                : suggestions.length + 1; // +1 for "Use my location"

              if (e.key === 'Escape') {
                setShowSuggestions(false);
                setHighlightedIndex(-1);
              } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlightedIndex((prev) => (prev + 1) % totalOptions);
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightedIndex((prev) => (prev - 1 + totalOptions) % totalOptions);
              } else if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                if (highlightedIndex === 0 && !disabledLiveLocation) {
                  handleCurrentLocationClick();
                } else if (highlightedIndex >= 0) {
                  // Select the suggestion and then call onEnter
                  const suggestion =
                    suggestions[disabledLiveLocation ? highlightedIndex : highlightedIndex - 1];
                  if (suggestion) {
                    await handleSuggestionClick(suggestion);
                    const details = await fetchGooglePlaceDetails(
                      suggestion?.place_id || '',
                      placesSessionId
                    );
                    if (details && details.geometry) {
                      const selectedLocation: Location = {
                        address: details.formatted_address,
                        lat: details.geometry.location.lat,
                        lng: details.geometry.location.lng,
                      };
                      onEnter?.(selectedLocation);
                    }
                  }
                } else if (location) {
                  // No suggestion highlighted, but a location is selected
                  onEnter?.(location);
                }
                // If no suggestion is highlighted and no location, do nothing
              }
            }}
            className={`w-full text-18 leading-28 text-gray-900 focus:outline-2 focus:outline-blue-base truncate max-w-full whitespace-nowrap ${
              designType === 'mini'
                ? 'rounded-l-sm border-2 border-gray-100 py-7 px-8'
                : 'lg:rounded-tr-sm rounded-bl-sm lg:rounded-bl-none rounded-br-sm px-6 pt-[30px] pb-5'
            }`}
            autoComplete="off"
            id="locationInput"
            placeholder={designType === 'mini' ? placeholder?.value : ''}
            aria-activedescendant={
              highlightedIndex === 0 && !disabledLiveLocation
                ? 'suggestion-use-my-location'
                : highlightedIndex > 0 && !disabledLiveLocation
                  ? `suggestion-${suggestions[highlightedIndex - 1]?.place_id}`
                  : highlightedIndex >= 0 && disabledLiveLocation
                    ? `suggestion-${suggestions[highlightedIndex]?.place_id}`
                    : undefined
            }
          />
          {designType === 'default' && (
            <label
              htmlFor="locationInput"
              className={`absolute left-6 transition-all text-gray-900 duration-200 pointer-events-none ${
                inputValue || isConditionFocused
                  ? 'text-14 leading-16 top-[10px]'
                  : 'text-18 leading-28 top-[20px]'
              }`}
            >
              <PlainTextWrapper field={placeholder} />
            </label>
          )}

          {inputValue && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-gray-500 text-white rounded-full w-[24px] h-[24px] flex items-center justify-center"
              onClick={() => {
                setInputValue('');
                setSuggestions([]);
                setShowSuggestions(false);
                setLocation(null);
                inputRef.current?.blur();
                onClear?.();
                setIsConditionFocused(false);
              }}
              aria-label="Clear input"
              type="button"
            >
              <SvgIcon icon="close" />
            </button>
          )}

          {showSuggestions && (
            <ul
              ref={suggestionListRef}
              className={`absolute shadow-default z-10 max-h-[240px] bg-white left-0 w-full border-2 border-gray-100 rounded-sm overflow-auto p-2 ${
                designType === 'mini' ? 'top-[66px]' : 'top-[77px]'
              }`}
              aria-label="Location suggestions"
              tabIndex={-1}
              role="listbox"
            >
              {!disabledLiveLocation && (
                <li
                  ref={(el) => {
                    if (highlightedIndex === 0 && el) {
                      el.scrollIntoView({ block: 'nearest' });
                    }
                  }}
                  id="suggestion-use-my-location"
                  role="option"
                  aria-selected={highlightedIndex === 0}
                >
                  <ButtonWrapper
                    ctaVariant="hyperlink"
                    className={`px-8 py-5 w-full !rounded-[1px] text-blue-base hover:bg-blue-50 underline hover:cursor-pointer flex items-center gap-4 ${
                      highlightedIndex === 0 ? 'bg-blue-50 font-bold' : ''
                    }`}
                    onClick={() => handleCurrentLocationClick()}
                  >
                    <SvgIcon icon="gpsfix" size="md" />
                    <PlainTextWrapper field={{ value: 'Use my location' }} />
                  </ButtonWrapper>
                </li>
              )}
              {suggestions.map((s, index) => {
                const isHighlighted = disabledLiveLocation
                  ? index === highlightedIndex
                  : index + 1 === highlightedIndex;
                // Highlight matching part
                const input = inputValue.trim();
                const desc = s.description;
                const matchIndex = input ? desc.toLowerCase().indexOf(input.toLowerCase()) : -1;
                const before = desc.slice(0, matchIndex);
                const match = desc.slice(matchIndex, matchIndex + input.length);
                const after = desc.slice(matchIndex + input.length);

                return (
                  <li
                    key={s.place_id}
                    ref={(el) => {
                      if (isHighlighted && el) {
                        el.scrollIntoView({ block: 'nearest' });
                      }
                    }}
                    className={`px-8 py-5 hover:bg-blue-50 hover:underline hover:cursor-pointer ${
                      isHighlighted ? 'bg-blue-50 font-bold' : ''
                    }`}
                    onMouseDown={() => handleSuggestionClick(s)}
                    onMouseEnter={() =>
                      setHighlightedIndex(disabledLiveLocation ? index : index + 1)
                    }
                    role="option"
                    aria-selected={isHighlighted}
                  >
                    {matchIndex !== -1 ? (
                      <>
                        {before}
                        <span className="font-bold">{match}</span>
                        {after}
                      </>
                    ) : (
                      desc
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    );
  }
);

LocationInput.displayName = 'LocationInput';

export default LocationInput;
